import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronUp } from "lucide-react";

interface PaymentsTableProps {
  customers: Customer[];
  products: Product[];
  jobs: string[];
  onAddProduct: (product: Omit<Product, "id" | "createdAt" | "remainingAmount">) => void;
  onDeleteProduct: (id: string) => void;
  onUpdateProduct: (product: Product) => void;
  onNavigate?: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "settings") => void;
  onSelectCustomer?: (customer: Customer) => void;
}

export function PaymentsTable({ customers, products, jobs, onAddProduct, onDeleteProduct, onUpdateProduct, onNavigate, onSelectCustomer }: PaymentsTableProps) {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());

  const toggleExpanded = (customerId: string) => {
    setExpandedCustomers(prev => {
      const newSet = new Set<string>();
      if (!prev.has(customerId)) {
        newSet.add(customerId);
      }
      return newSet;
    });
  };

  const handleViewCustomer = (customer: Customer) => {
    if (onSelectCustomer) {
      onSelectCustomer(customer);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortField(null);
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
    }
    return sortDirection === "asc" 
      ? <ArrowUp className="h-3 w-3 text-primary" />
      : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  // Calculate totals for each customer
  const customersWithTotals = customers.map(customer => {
    const customerProducts = products.filter(p => p.customerId === customer.id);
    
    const totalProducts = customerProducts.length;
    
    // IQD totals
    const iqdProducts = customerProducts.filter(p => p.currency === "IQD");
    const totalProductsIQD = iqdProducts.length;
    const totalSaleIQD = iqdProducts.reduce((sum, p) => sum + p.salePrice, 0);
    const totalPaidIQD = iqdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
    const totalRemainingIQD = iqdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);
    
    // USD totals
    const usdProducts = customerProducts.filter(p => p.currency === "USD");
    const totalProductsUSD = usdProducts.length;
    const totalSaleUSD = usdProducts.reduce((sum, p) => sum + p.salePrice, 0);
    const totalPaidUSD = usdProducts.reduce((sum, p) => sum + p.paidAmount, 0);
    const totalRemainingUSD = usdProducts.reduce((sum, p) => sum + p.remainingAmount, 0);

    return {
      ...customer,
      totalProducts,
      totalProductsIQD,
      totalProductsUSD,
      totalSaleIQD,
      totalPaidIQD,
      totalRemainingIQD,
      totalSaleUSD,
      totalPaidUSD,
      totalRemainingUSD,
    };
  });

  // Filter only customers with products
  const customersWithProducts = customersWithTotals.filter(c => c.totalProducts > 0);

  // Sort customers
  const sortedCustomers = [...customersWithProducts].sort((a, b) => {
    if (!sortField) return 0;
    
    let aValue: string | number = "";
    let bValue: string | number = "";
    
    switch (sortField) {
      case "code":
        aValue = a.customerCode;
        bValue = b.customerCode;
        break;
      case "name":
        aValue = a.fullName;
        bValue = b.fullName;
        break;
      case "products":
        aValue = a.totalProducts;
        bValue = b.totalProducts;
        break;
      case "sale":
        aValue = a.totalSaleIQD + (a.totalSaleUSD * 1500);
        bValue = b.totalSaleIQD + (b.totalSaleUSD * 1500);
        break;
      case "paid":
        aValue = a.totalPaidIQD + (a.totalPaidUSD * 1500);
        bValue = b.totalPaidIQD + (b.totalPaidUSD * 1500);
        break;
      case "remaining":
        aValue = a.totalRemainingIQD + (a.totalRemainingUSD * 1500);
        bValue = b.totalRemainingIQD + (b.totalRemainingUSD * 1500);
        break;
    }
    
    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    }
    
    return sortDirection === "asc" 
      ? String(aValue).localeCompare(String(bValue), "ar")
      : String(bValue).localeCompare(String(aValue), "ar");
  });

  if (sortedCustomers.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        هیچ پارەدانێک نییە
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table */}
      <div className="rounded-md border hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "code" ? "bg-muted/30" : ""}`} onClick={() => handleSort("code")}>
                <div className="flex items-center gap-1">
                  کۆد
                  <SortIcon field="code" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "name" ? "bg-muted/30" : ""}`} onClick={() => handleSort("name")}>
                <div className="flex items-center gap-1">
                  ناو
                  <SortIcon field="name" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "products" ? "bg-muted/30" : ""}`} onClick={() => handleSort("products")}>
                <div className="flex items-center gap-1">
                  کۆی کاڵاکان
                  <SortIcon field="products" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "sale" ? "bg-muted/30" : ""}`} onClick={() => handleSort("sale")}>
                <div className="flex items-center gap-1">
                  کۆی فرۆشتنەکان
                  <SortIcon field="sale" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "paid" ? "bg-muted/30" : ""}`} onClick={() => handleSort("paid")}>
                <div className="flex items-center gap-1">
                  بڕی وەرگیراو
                  <SortIcon field="paid" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "remaining" ? "bg-muted/30" : ""}`} onClick={() => handleSort("remaining")}>
                <div className="flex items-center gap-1">
                  بڕی ماوە
                  <SortIcon field="remaining" />
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedCustomers.map((customer, index) => (
              <TableRow 
                key={customer.id} 
                className={`cursor-pointer h-16 ${index % 2 === 1 ? 'bg-muted/50' : ''}`}
                onClick={() => handleViewCustomer(customer)}
              >
                <TableCell className="py-2">
                  <Badge variant="outline">{customer.customerCode}</Badge>
                </TableCell>
                <TableCell className="font-medium py-2">{customer.fullName}</TableCell>
                <TableCell className="py-2">
                  <div className="space-y-0.5">
                    {customer.totalProductsIQD > 0 && (
                      <div className="text-right">
                        {customer.totalProductsIQD}
                      </div>
                    )}
                    {customer.totalProductsUSD > 0 && (
                      <div className="text-right">
                        {customer.totalProductsUSD}
                      </div>
                    )}
                    {customer.totalProductsIQD === 0 && customer.totalProductsUSD === 0 && (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <div className="space-y-0.5">
                    {customer.totalSaleIQD > 0 && (
                      <div dir="ltr" className="text-right">
                        {customer.totalSaleIQD.toLocaleString()}{" "}
                        <Badge variant="outline" className="mr-1">د.ع</Badge>
                      </div>
                    )}
                    {customer.totalSaleUSD > 0 && (
                      <div dir="ltr" className="text-right">
                        {customer.totalSaleUSD.toLocaleString()}{" "}
                        <Badge variant="outline" className="mr-1">$</Badge>
                      </div>
                    )}
                    {customer.totalSaleIQD === 0 && customer.totalSaleUSD === 0 && (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <div className="space-y-0.5">
                    {customer.totalPaidIQD > 0 && (
                      <div dir="ltr" className="text-right text-green-600">
                        {customer.totalPaidIQD.toLocaleString()}{" "}
                        <Badge variant="secondary" className="mr-1">د.ع</Badge>
                      </div>
                    )}
                    {customer.totalPaidUSD > 0 && (
                      <div dir="ltr" className="text-right text-green-600">
                        {customer.totalPaidUSD.toLocaleString()}{" "}
                        <Badge variant="secondary" className="mr-1">$</Badge>
                      </div>
                    )}
                    {customer.totalPaidIQD === 0 && customer.totalPaidUSD === 0 && (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <div className="space-y-0.5">
                    {customer.totalRemainingIQD > 0 && (
                      <div dir="ltr" className="text-right">
                        {customer.totalRemainingIQD.toLocaleString()}{" "}
                        <Badge variant="destructive" className="mr-1">د.ع</Badge>
                      </div>
                    )}
                    {customer.totalRemainingUSD > 0 && (
                      <div dir="ltr" className="text-right">
                        {customer.totalRemainingUSD.toLocaleString()}{" "}
                        <Badge variant="destructive" className="mr-1">$</Badge>
                      </div>
                    )}
                    {customer.totalRemainingIQD === 0 && customer.totalRemainingUSD === 0 && (
                      <Badge variant="outline" className="text-green-600 border-green-600">تەواو</Badge>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {sortedCustomers.map((customer, index) => {
          const isExpanded = expandedCustomers.has(customer.id);
          return (
            <div key={customer.id} className={`rounded-xl border bg-card overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${index % 2 === 1 ? 'bg-muted/60' : ''}`}>
              <div className="p-3 cursor-pointer" onClick={() => toggleExpanded(customer.id)}>
                {/* Main Row */}
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary">{customer.customerCode}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm truncate">{customer.fullName}</span>
                      <Badge variant="secondary" className="text-[10px] px-1.5 h-4">
                        {customer.totalProducts} کاڵا
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {customer.phone1}
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpanded(customer.id);
                    }}
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {/* Mini Table - IQD */}
                {customer.totalProductsIQD > 0 && (
                  <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2 w-full" dir="rtl">
                    <div className="text-center">
                      <div className="text-muted-foreground text-[10px]">فرۆشتن</div>
                      <div className="font-semibold" dir="rtl">{customer.totalSaleIQD.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></div>
                    </div>
                    <div className="text-center border-x border-dashed">
                      <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                      <div className="font-semibold text-green-600" dir="rtl">{customer.totalPaidIQD.toLocaleString()} <span className="text-[10px]">د.ع</span></div>
                    </div>
                    <div className="text-center">
                      <div className="text-muted-foreground text-[10px]">ماوە</div>
                      <div className="font-semibold text-red-500" dir="rtl">{customer.totalRemainingIQD.toLocaleString()} <span className="text-[10px]">د.ع</span></div>
                    </div>
                  </div>
                )}
                {/* Mini Table - USD */}
                {customer.totalProductsUSD > 0 && (
                  <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-emerald-500/10 rounded-lg p-2 w-full" dir="rtl">
                    <div className="text-center">
                      <div className="text-muted-foreground text-[10px]">فرۆشتن</div>
                      <div className="font-semibold" dir="ltr"><span className="text-[10px] text-muted-foreground">$ </span>{customer.totalSaleUSD.toLocaleString()}</div>
                    </div>
                    <div className="text-center border-x border-dashed">
                      <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                      <div className="font-semibold text-green-600" dir="ltr"><span className="text-[10px]">$ </span>{customer.totalPaidUSD.toLocaleString()}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-muted-foreground text-[10px]">ماوە</div>
                      <div className="font-semibold text-red-500" dir="ltr"><span className="text-[10px]">$ </span>{customer.totalRemainingUSD.toLocaleString()}</div>
                    </div>
                  </div>
                )}
              </div>
              {isExpanded && (
                <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                  <div className="space-y-2 pt-3">
                    <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                      <span className="text-muted-foreground text-xs">ژمارەی کاڵاکان</span>
                      <span className="font-medium text-sm">{customer.totalProducts}</span>
                    </div>
                    {customer.job && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">پیشە</span>
                        <span className="font-medium text-sm">{customer.job}</span>
                      </div>
                    )}
                    {customer.address && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">ناونیشان</span>
                        <span className="font-medium text-sm">{customer.address}</span>
                      </div>
                    )}
                    <Button 
                      size="sm" 
                      className="w-full mt-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewCustomer(customer);
                      }}
                    >
                      پارەدان
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
